import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { adminApi } from '@/lib/adminApi';
import { INVITE_STATUS_HEX, inviteStatus, type Invite, type Tester } from '@/lib/testers';
import { strataLabel } from '@/lib/periReview';
import { AdminPage, RegistryTable, Pill, StatusPill, EntityFormModal, type FieldSpec } from '@/admin';

// Perimenopause-review cohort assignment picker (source of truth = the
// peri_review_cohort store; this writes it via /admin/testers/{ref}/cohort).
const COHORT_FIELDS: FieldSpec[] = [
  { name: 'membership', label: 'Membership', type: 'select', options: [
    { value: 'in', label: 'In review cohort' }, { value: 'out', label: 'Not in cohort' }] },
  { name: 'review_mode', label: 'Review mode', type: 'select', options: [
    { value: 'open', label: 'open' }, { value: 'blinded', label: 'blinded' }] },
  { name: 'strata_stage', label: 'Transition stage', type: 'select', options: [
    { value: 'early_transition', label: 'early transition' }, { value: 'late_transition', label: 'late transition' }] },
  { name: 'strata_subbranch', label: 'Sub-branch', type: 'select', options: [
    { value: 'base', label: 'base' }, { value: 'nd_affirming', label: 'ND-affirming' }] },
];

function testerStatus(t: Tester): { label: string; on: boolean } {
  if (t.revoked) return { label: 'revoked', on: false };
  if (!t.onboarding_complete) return { label: 'onboarding', on: false };
  return { label: 'active', on: true };
}

export function TestersPage() {
  const qc = useQueryClient();
  const invites = useQuery({ queryKey: ['invites'], queryFn: () => adminApi.invites.list() });
  const testers = useQuery({ queryKey: ['testers'], queryFn: () => adminApi.testers.list() });
  const refreshInvites = () => void qc.invalidateQueries({ queryKey: ['invites'] });
  const refreshTesters = () => void qc.invalidateQueries({ queryKey: ['testers'] });

  // Product picker options come from the mode registry rather than a hardcoded
  // list, so a mode authored into the registry is mintable without a console
  // change (and a retired one stops being offered).
  const modes = useQuery({ queryKey: ['modes'], queryFn: () => adminApi.modes.list() });
  const inviteFields: FieldSpec[] = [
    { name: 'invitee_email', label: 'Invitee email', type: 'text', placeholder: 'name@example.com' },
    { name: 'mode_id', label: 'Product', type: 'select',
      hint: 'The invitee lands in this product on redeem — there is no user-facing selector to change it.',
      options: (modes.data?.modes ?? []).map((m) => ({ value: m.mode_id, label: m.display_name || m.mode_id })) },
  ];

  const [invitingOpen, setInvitingOpen] = useState(false);
  const [inviteErr, setInviteErr] = useState<string | null>(null);
  const createInvite = useMutation({
    mutationFn: (v: { invitee_email: string; mode_id: string }) => adminApi.invites.create(v.invitee_email, v.mode_id),
    onSuccess: (r) => { setInvitingOpen(false); setInviteErr(null); refreshInvites(); alert(`Invite created:\n${r.invite_url}`); },
    onError: (e: unknown) => setInviteErr(e instanceof Error ? e.message : 'Error'),
  });
  const sendInvite = useMutation({
    mutationFn: (i: Invite) => adminApi.invites.send(i.code, i.invitee_email ?? '', i.invite_url),
    onSuccess: () => alert('Invite sent.'),
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Error'),
  });
  const revokeInvite = useMutation({ mutationFn: (code: string) => adminApi.invites.revoke(code), onSuccess: refreshInvites, onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Error') });
  const revokeTester = useMutation({ mutationFn: (ref: string) => adminApi.testers.revoke(ref), onSuccess: refreshTesters, onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Error') });
  const reinstateTester = useMutation({ mutationFn: (ref: string) => adminApi.testers.reinstate(ref), onSuccess: refreshTesters, onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Error') });

  const [cohortFor, setCohortFor] = useState<Tester | null>(null);
  const [cohortErr, setCohortErr] = useState<string | null>(null);
  const setCohort = useMutation({
    mutationFn: (a: { ref: string; body: { in_cohort: boolean; review_mode: string; strata_stage: string; strata_subbranch: string } }) =>
      adminApi.testers.setCohort(a.ref, a.body),
    onSuccess: () => { setCohortFor(null); setCohortErr(null); refreshTesters(); },
    onError: (e: unknown) => setCohortErr(e instanceof Error ? e.message : 'Error'),
  });

  return (
    <AdminPage
      title="Testers & invites"
      description="Beta access — issue single-use invites and manage tester accounts."
      actions={<Button size="sm" onClick={() => { setInviteErr(null); setInvitingOpen(true); }}>New invite</Button>}
    >
      <h2 className="mb-8 text-h3 font-semibold">Invites</h2>
      <div className="mb-21">
        <RegistryTable<Invite>
          columns={[
            { key: 'code', header: 'Code', className: 'font-mono text-[12px]', render: (i) => i.code },
            { key: 'for', header: 'For', className: 'text-[12px]', render: (i) => i.invitee_email || '—' },
            // Pre-ee1b412c invites carry no mode_id; they redeem mode-less and the
            // invitee has no selector to recover, so surface that as a gap not a blank.
            { key: 'product', header: 'Product', className: 'text-[12px]', render: (i) => i.mode_id
              ? (modes.data?.modes?.find((m) => m.mode_id === i.mode_id)?.display_name ?? i.mode_id)
              : <span className="text-text-muted">none (legacy)</span> },
            { key: 'expires', header: 'Expires', className: 'text-[12px] text-text-secondary', render: (i) => i.expires_at ? i.expires_at.slice(0, 10) : '—' },
            { key: 'status', header: 'Status', render: (i) => { const s = inviteStatus(i); return <Pill label={s} color={INVITE_STATUS_HEX[s]} />; } },
          ]}
          rows={invites.data ?? []}
          getRowKey={(i) => i.code}
          count={invites.data?.length}
          state={{ loading: invites.isLoading, error: invites.isError }}
          emptyText="No invites yet."
          rowActions={(i) => {
            const s = inviteStatus(i);
            return (
              <div className="flex items-center gap-2">
                {s === 'pending' ? <Button variant="outline" size="sm" disabled={sendInvite.isPending} onClick={() => sendInvite.mutate(i)}>Send</Button> : null}
                {s === 'pending' ? <Button variant="destructive" size="sm" onClick={() => { if (confirm(`Revoke invite ${i.code}?`)) revokeInvite.mutate(i.code); }}>Revoke</Button> : null}
              </div>
            );
          }}
        />
      </div>

      <h2 className="mb-8 text-h3 font-semibold">Testers</h2>
      <RegistryTable<Tester>
        columns={[
          // THE REF BESIDE THE NAME (owner, 2026-10-12): new accounts get opaque refs
          // (staging-u-…), so the ref no longer says who it is and the name no longer
          // says which account. Both, as the orders page's Person shows them.
          { key: 'name', header: 'Name', className: 'font-medium', render: (t) => (
            <span data-testid={`tester-${t.user_ref}`}>
              {t.name || <span className="text-text-muted">—</span>}
              <span data-testid="tester-ref" className="block font-mono text-[11px] font-normal text-text-muted">{t.user_ref}</span>
            </span>
          ) },
          { key: 'email', header: 'Email', className: 'text-[12px] text-text-secondary', render: (t) => t.email || '—' },
          { key: 'joined', header: 'Joined', className: 'text-[12px] text-text-secondary', render: (t) => t.created_at ? t.created_at.slice(0, 10) : '—' },
          { key: 'docs', header: 'Docs', className: 'font-mono text-[12px]', render: (t) => t.documents_ingested ?? 0 },
          { key: 'board', header: 'Board', className: 'font-mono text-[12px]', render: (t) => t.board_sessions ?? 0 },
          { key: 'cohort', header: 'Peri cohort', className: 'text-[12px]', render: (t) => (
            t.cohort
              ? <span><Pill label={t.cohort.review_mode} color={t.cohort.review_mode === 'blinded' ? '#5457d6' : '#8A94A6'} /> <span className="text-text-secondary">{strataLabel(t.cohort.strata_stage, t.cohort.strata_subbranch)}</span></span>
              : <span className="text-text-muted">—</span>
          ) },
          { key: 'status', header: 'Status', render: (t) => { const s = testerStatus(t); return <StatusPill on={s.on} label={s.label} />; } },
        ]}
        rows={testers.data ?? []}
        getRowKey={(t) => t.user_ref}
        count={testers.data?.length}
        state={{ loading: testers.isLoading, error: testers.isError }}
        emptyText="No testers yet."
        rowActions={(t) => (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { setCohortErr(null); setCohortFor(t); }}>Cohort</Button>
            {t.revoked
              ? <Button variant="outline" size="sm" disabled={reinstateTester.isPending} onClick={() => reinstateTester.mutate(t.user_ref)}>Reinstate</Button>
              : <Button variant="destructive" size="sm" onClick={() => { if (confirm(`Revoke ${t.name || t.user_ref}'s access?`)) revokeTester.mutate(t.user_ref); }}>Revoke</Button>}
          </div>
        )}
      />

      {cohortFor ? (
        <EntityFormModal
          title={`Peri-review cohort · ${cohortFor.name || cohortFor.user_ref}`}
          mode={cohortFor.cohort ? 'edit' : 'add'}
          fields={COHORT_FIELDS}
          initialValues={{
            membership: cohortFor.cohort ? 'in' : 'out',
            review_mode: cohortFor.cohort?.review_mode ?? 'open',
            strata_stage: cohortFor.cohort?.strata_stage ?? 'early_transition',
            strata_subbranch: cohortFor.cohort?.strata_subbranch ?? 'base',
          }}
          error={cohortErr}
          busy={setCohort.isPending}
          onClose={() => setCohortFor(null)}
          onSubmit={(v) => setCohort.mutate({
            ref: cohortFor.user_ref,
            body: {
              in_cohort: v.membership === 'in',
              review_mode: String(v.review_mode),
              strata_stage: String(v.strata_stage),
              strata_subbranch: String(v.strata_subbranch),
            },
          })}
        />
      ) : null}

      {invitingOpen ? (
        <EntityFormModal
          title="New invite"
          mode="add"
          fields={inviteFields}
          initialValues={{ invitee_email: '', mode_id: '' }}
          error={inviteErr ?? (modes.isError ? 'Could not load products — cannot mint without one.' : null)}
          busy={createInvite.isPending}
          onClose={() => setInvitingOpen(false)}
          onSubmit={(v) => createInvite.mutate({ invitee_email: String(v.invitee_email).trim(), mode_id: String(v.mode_id) })}
        />
      ) : null}
    </AdminPage>
  );
}
